<!-- Parsec Cloud (https://parsec.cloud) Copyright (c) AGPL-3.0-only 2016-present Scille SAS -->

<template>
  <ion-page v-if="initialized">
    <ion-router-outlet />
  </ion-page>
</template>

<script setup lang="ts">
import * as parsec from '@/parsec';
import { getClientConfig } from '@/parsec/internals';
import { libparsec, ParsecInvitationAddr } from '@/plugins/libparsec';
import { getConnectionHandle, navigateTo, Routes } from '@/router';
import { EventDistributor } from '@/services/eventDistributor';
import { InjectionProvider, InjectionProviderKey } from '@/services/injectionProvider';
import { StorageManager, StorageManagerKey } from '@/services/storageManager';
import { IonPage, IonRouterOutlet } from '@ionic/vue';
import { inject, onMounted, ref } from 'vue';

const injectionProvider: InjectionProvider = inject(InjectionProviderKey)!;
const storageManager: StorageManager = inject(StorageManagerKey)!;
const initialized = ref(false);

const DEFAULT_HANDLE = 1;
const DEFAULT_HANDLE_WITH_PARSEC_ACCOUNT = 2;

function getDevDefaultHandle(): parsec.ConnectionHandle {
  if (parsec.ParsecAccount.isLoggedIn()) {
    return DEFAULT_HANDLE_WITH_PARSEC_ACCOUNT;
  }
  return DEFAULT_HANDLE;
}

onMounted(async () => {
  initialized.value = false;
  const handle = getConnectionHandle();

  if (!handle) {
    window.nativeAPI.log('error', 'Failed to retrieve connection handle while logged in');
    await navigateTo(Routes.Home, { replace: true, skipHandle: true });
    return;
  }

  if (window.isTesting()) {
    if ((window as any).TESTING_ADD_USERS) {
      const users = ((window as any).TESTING_ADD_USERS as string).split(';').map((userWithProfile) => {
        const [user, profile] = userWithProfile.split(':');
        return { label: user, email: `${user.replace(' ', '_')}@invalid.com`, profile: profile as parsec.UserProfile, revoked: false };
      });
      if (users) {
        await populateUsers(handle, users);
      }
    }
  }

  if (
    !import.meta.env.PARSEC_APP_TESTBED_SERVER ||
    window.isTesting() ||
    (await parsec.getClientInfo(handle)).ok === true ||
    import.meta.env.PARSEC_APP_TESTBED_AUTO_LOGIN !== 'true'
  ) {
    initialized.value = true;
    return;
  }

  // When in dev mode, we often open directly a connected page,
  // so a few states are not properly set.
  if (parsec.ParsecAccount.isLoggedIn()) {
    window.nativeAPI.log('info', `Parsec Account logged in, default handle should be "${DEFAULT_HANDLE_WITH_PARSEC_ACCOUNT}"`);
  }
  if (handle !== getDevDefaultHandle()) {
    window.nativeAPI.log('error', `In dev mode, you should use "${getDevDefaultHandle()}" as the default handle`);
    // eslint-disable-next-line no-alert
    alert(
      `Use "${DEFAULT_HANDLE}" or "${DEFAULT_HANDLE_WITH_PARSEC_ACCOUNT}" (if using ParsecAccount)
as the default handle when not connecting properly`,
    );
    await navigateTo(Routes.Home);
    return;
  }
  window.nativeAPI.log('info', 'Page was refreshed, login in a default device');
  injectionProvider.createNewInjections(getDevDefaultHandle(), new EventDistributor());
  // Not filtering the devices, because we need alice first device, not the second one
  const listResult = await parsec.listAvailableDevicesWithError(false);
  let devices: Array<parsec.AvailableDevice> = [];
  if (!listResult.ok) {
    window.nativeAPI.log('error', `Error when listing the devices: ${listResult.error.tag} (${listResult.error.error})`);
  } else {
    devices = listResult.value;
  }

  let device = devices.find((d) => d.humanHandle.label === 'Alicey McAliceFace' && d.deviceLabel.includes('dev1'));

  if (!device) {
    device = devices[0];
    window.nativeAPI.log('error', `Could not find Alice device, using ${device.humanHandle.label}`);
  }

  const result = await parsec.login(
    device,
    parsec.constructAccessStrategy(device, parsec.PrimaryProtectionStrategy.usePassword('P@ssw0rd.')),
  );
  if (!result.ok) {
    window.nativeAPI.log('error', `Failed to log in on a default device: ${JSON.stringify(result.error)}`);
  } else if (result.value !== getDevDefaultHandle()) {
    window.nativeAPI.log('error', `Lib returned handle ${result.value} instead of ${getDevDefaultHandle()}`);
  } else {
    window.nativeAPI.log('info', `Logged in as ${device.humanHandle.label}`);
  }

  if (import.meta.env.PARSEC_APP_CLEAR_CACHE === 'true') {
    await storageManager.clearAll();
  }
  await populate(handle);
  initialized.value = true;
});

async function populate(handle: parsec.ConnectionHandle): Promise<void> {
  if (import.meta.env.PARSEC_APP_DEV_POPULATE_WORKSPACES && !isNaN(Number(import.meta.env.PARSEC_APP_DEV_POPULATE_WORKSPACES))) {
    for (let i = 2; i < Number(import.meta.env.PARSEC_APP_DEV_POPULATE_WORKSPACES) + 2; i++) {
      window.nativeAPI.log('info', `Creating workspace 'wksp${i}'`);
      await parsec.createWorkspace(`wksp${i}`);
    }
  }

  const workspaces = await parsec.listWorkspaces(getConnectionHandle());
  if (!workspaces.ok) {
    window.nativeAPI.log('error', 'Failed to list workspaces');
    return;
  }

  for (const workspace of workspaces.value) {
    if (import.meta.env.PARSEC_APP_DEV_POPULATE_REAL_FILES === 'true') {
      await populateRealFiles(workspace);
    }
    if (import.meta.env.PARSEC_APP_DEV_POPULATE_MANY_FILES) {
      await populateManyFiles(workspace, import.meta.env.PARSEC_APP_DEV_POPULATE_MANY_FILES.split(';'));
    }
  }
  if (import.meta.env.PARSEC_APP_DEV_ADD_READONLY_WORKSPACE === 'true') {
    await addReadOnlyWorkspace();
  }

  if (import.meta.env.PARSEC_APP_DEV_POPULATE_USERS === 'true') {
    await populateUsers(handle);
  }
}

interface UserData {
  label: string;
  email: string;
  profile: parsec.UserProfile;
  revoked: boolean;
}

async function populateUsers(handle: parsec.ConnectionHandle, users: Array<UserData> | undefined = undefined): Promise<void> {
  const DEFAULT_USERS: Array<UserData> = [
    {
      // cspell:disable-next-line
      label: 'Gordon Freeman',
      // cspell:disable-next-line
      email: 'gordon.freeman@blackmesa.nm',
      profile: parsec.UserProfile.Standard,
      revoked: false,
    },
    {
      // cspell:disable-next-line
      label: 'Arthas Menethil',
      // cspell:disable-next-line
      email: 'arthas.menethil@lordaeron.az',
      profile: parsec.UserProfile.Admin,
      revoked: true,
    },
    {
      // cspell:disable-next-line
      label: 'Karlach',
      // cspell:disable-next-line
      email: 'karlach@avernus.hell',
      profile: parsec.UserProfile.Outsider,
      revoked: false,
    },
    {
      // cspell:disable-next-line
      label: 'Artorias',
      // cspell:disable-next-line
      email: 'artorias@abyss.dark',
      profile: parsec.UserProfile.Admin,
      revoked: false,
    },
  ];

  for (const user of users || DEFAULT_USERS) {
    await addUser(handle, user.label, user.email, user.profile, user.revoked);
  }
}

async function addUser(
  connHandle: parsec.ConnectionHandle,
  label: string,
  email: string,
  profile: parsec.UserProfile,
  revoke: boolean,
): Promise<void> {
  const invResult = await parsec.inviteUser(email);
  if (!invResult.ok) {
    return;
  }

  window.nativeAPI.log('debug', `Adding user ${label} <${email}>`);

  greetUser(connHandle, invResult.value.token, profile)
    .then(async () => {
      if (revoke) {
        window.nativeAPI.log('debug', `Revoking ${label} <${email}>`);
        const result = await parsec.listUsers(true, email);
        if (result.ok && result.value.length > 0) {
          await parsec.revokeUser(result.value[0].id);
        }
      }
    })
    .catch((err: string) => {
      window.nativeAPI.log('error', `Greet failed: ${err}`);
    });
  const [invitationAddr, _] = invResult.value.addr;
  claimUser(email, label, invitationAddr).catch((err: string) => {
    window.nativeAPI.log('error', `Claim failed: ${err}`);
  });
}

async function claimUser(email: string, label: string, invitationAddr: ParsecInvitationAddr): Promise<void> {
  const retResult = await libparsec.claimerRetrieveInfo(getClientConfig(), invitationAddr);
  if (!retResult.ok) {
    throw new Error(`claimerRetrieveInfo failed: ${retResult.error.error}`);
  }
  let handle = retResult.value.handle;
  let canceller = await libparsec.newCanceller();
  const waitResult = await libparsec.claimerUserWaitAllPeers(canceller, handle);
  if (!waitResult.ok) {
    throw new Error(`claimerUserWaitAllPeers failed: ${waitResult.error.error}`);
  }
  handle = waitResult.value.handle;
  canceller = await libparsec.newCanceller();
  const signifyTrustResult = await libparsec.claimerUserInProgress1DoSignifyTrust(canceller, handle);
  if (!signifyTrustResult.ok) {
    throw new Error(`claimerUserInProgress1DoSignifyTrust failed: ${signifyTrustResult.error.error}`);
  }
  handle = signifyTrustResult.value.handle;
  canceller = await libparsec.newCanceller();
  const waitTrustResult = await libparsec.claimerUserInProgress2DoWaitPeerTrust(canceller, handle);
  if (!waitTrustResult.ok) {
    throw new Error(`claimerUserInProgress2DoWaitPeerTrust failed: ${waitTrustResult.error.error}`);
  }
  handle = waitTrustResult.value.handle;
  canceller = await libparsec.newCanceller();
  const claimResult = await libparsec.claimerUserInProgress3DoClaim(canceller, handle!, 'DeviceLabel', { email: email, label: label });
  if (!claimResult.ok) {
    throw new Error(`claimerUserInProgress3DoClaim failed: ${claimResult.error.error}`);
  }
  handle = claimResult.value.handle;
  const finalizeResult = await libparsec.claimerUserFinalizeSaveLocalDevice(
    handle,
    parsec.constructSaveStrategy(parsec.PrimaryProtectionStrategy.usePassword('P@ssw0rd.')),
  );
  if (!finalizeResult.ok) {
    throw new Error(`claimerUserFinalizeSaveLocalDevice failed: ${finalizeResult.error.error}`);
  }
}

async function greetUser(connHandle: parsec.ConnectionHandle, token: string, profile: parsec.UserProfile): Promise<void> {
  const greetResult = await libparsec.clientStartUserInvitationGreet(connHandle, token);
  if (!greetResult.ok) {
    throw new Error(`clientStartUserInvitationGreet failed: ${greetResult.error.error}`);
  }
  let handle = greetResult.value.handle;
  let canceller = await libparsec.newCanceller();
  const waitResult = await libparsec.greeterUserInitialDoWaitPeer(canceller, handle);
  if (!waitResult.ok) {
    throw new Error(`greeterUserInitialDoWaitPeer failed: ${waitResult.error.error}`);
  }
  handle = waitResult.value.handle;
  canceller = await libparsec.newCanceller();
  const waitTrustResult = await libparsec.greeterUserInProgress1DoWaitPeerTrust(canceller, handle);
  if (!waitTrustResult.ok) {
    throw new Error(`greeterUserInProgress1DoWaitPeerTrust failed: ${waitTrustResult.error.error}`);
  }
  handle = waitTrustResult.value.handle;
  canceller = await libparsec.newCanceller();
  const signifyTrustResult = await libparsec.greeterUserInProgress2DoSignifyTrust(canceller, handle);
  if (!signifyTrustResult.ok) {
    throw new Error(`greeterUserInProgress2DoSignifyTrust failed: ${signifyTrustResult.error.error}`);
  }
  handle = signifyTrustResult.value.handle;
  canceller = await libparsec.newCanceller();
  const claimRequestsResult = await libparsec.greeterUserInProgress3DoGetClaimRequests(canceller, handle);
  if (!claimRequestsResult.ok) {
    throw new Error(`greeterUserInProgress3DoGetClaimRequests failed: ${claimRequestsResult.error.error}`);
  }
  handle = claimRequestsResult.value.handle;
  canceller = await libparsec.newCanceller();
  const createResult = await libparsec.greeterUserInProgress4DoCreate(
    canceller,
    handle,
    claimRequestsResult.value.requestedHumanHandle,
    claimRequestsResult.value.requestedDeviceLabel,
    profile,
  );
  if (!createResult.ok) {
    throw new Error(`greeterUserInProgress4DoCreate failed: ${createResult.error.error}`);
  }
}

async function populateManyFiles(workspace: parsec.WorkspaceInfo, treeDefinition = [2, 8, 10]): Promise<void> {
  // Avoid importing if unnecessary
  const { uniqueNamesGenerator, adjectives, colors, animals } = await import('unique-names-generator');

  let filesCreated = 0;
  const AVAILABLE_EXTENSIONS = ['png', 'docx', 'xlsx', 'mp4', 'mp3', 'pdf', 'txt', 'pptx'];

  window.nativeAPI.log('debug', `Creating mock arborescence in workspace ${workspace.name}`);
  for (let i = 0; i < treeDefinition[0]; i++) {
    const folder1Name = uniqueNamesGenerator({ dictionaries: [adjectives, colors, animals] });
    await parsec.createFolder(workspace.handle, `/${folder1Name}`);
    const fileName = uniqueNamesGenerator({ dictionaries: [adjectives, colors, animals] });
    const extension1 = AVAILABLE_EXTENSIONS[Math.floor(Math.random() * AVAILABLE_EXTENSIONS.length)];
    await parsec.createFile(workspace.handle, `/${fileName}.${extension1}`);
    for (let j = 0; j < treeDefinition[1]; j++) {
      const folder2Name = uniqueNamesGenerator({ dictionaries: [adjectives, colors, animals] });
      await parsec.createFolder(workspace.handle, `/${folder1Name}/${folder2Name}`);
      for (let k = 0; k < treeDefinition[2]; k++) {
        const file2Name = uniqueNamesGenerator({ dictionaries: [adjectives, colors, animals] });
        const extension2 = AVAILABLE_EXTENSIONS[Math.floor(Math.random() * AVAILABLE_EXTENSIONS.length)];
        await parsec.createFile(workspace.handle, `/${folder1Name}/${folder2Name}/${file2Name}.${extension2}`);
        filesCreated += 1;
        if (filesCreated % 20 === 0) {
          window.nativeAPI.log('debug', `${filesCreated}/${treeDefinition[0] * treeDefinition[1] * treeDefinition[2]}`);
        }
      }
    }
  }
}

async function populateRealFiles(workspace: parsec.WorkspaceInfo): Promise<void> {
  // Avoid importing files if unnecessary
  const mockFiles = await import('@/parsec/mock_files');

  window.nativeAPI.log('debug', `Creating mock files in workspace ${workspace.name}`);
  await parsec.createFolder(workspace.handle, '/Folder_éèñÑ');

  for (const fileType in mockFiles.MockFileType) {
    const fileName = `document_${fileType}.${fileType.toLocaleLowerCase()}`;
    const openResult = await parsec.openFile(workspace.handle, `/${fileName}`, {
      write: true,
      truncate: true,
      create: true,
      createNew: true,
    });

    if (!openResult.ok) {
      window.nativeAPI.log('error', `Could not open file ${fileName}`);
      continue;
    }
    try {
      const content = await mockFiles.getMockFileContent(fileType as any);
      const writeResult = await parsec.writeFile(workspace.handle, openResult.value, 0, content);
      if (!writeResult.ok) {
        window.nativeAPI.log('error', `Failed to write file ${fileName}`);
        continue;
      }
    } finally {
      await parsec.closeFile(workspace.handle, openResult.value);
    }
  }
}

async function addReadOnlyWorkspace(): Promise<void> {
  const devices = await parsec.listAvailableDevices(false);
  const bobDevice = devices.find((d) => d.humanHandle.label === 'Boby McBobFace');
  const aliceDevice = devices.find((d) => d.humanHandle.label === 'Alicey McAliceFace');

  window.nativeAPI.log('debug', 'Creating read-only workspace');

  if (!bobDevice || !aliceDevice) {
    window.nativeAPI.log('error', 'Could not find Alice or Bob device');
    return;
  }
  const loginResult = await libparsec.clientStart(
    getClientConfig(),
    parsec.constructAccessStrategy(bobDevice, parsec.PrimaryProtectionStrategy.usePassword('P@ssw0rd.')),
  );
  if (!loginResult.ok) {
    window.nativeAPI.log('error', `Failed to login as Bob: ${loginResult.error.error}`);
    return;
  }
  try {
    const wkResult = await libparsec.clientCreateWorkspace(loginResult.value, 'wksp-ro');
    if (!wkResult.ok) {
      window.nativeAPI.log('error', `Failed to create a workspace as Bob: ${wkResult.error.error}`);
      return;
    }

    /* Does not work currently because of some problems with the testbed */
    // const mockFiles = await import('@/parsec/mock_files');

    // window.nativeAPI.log('debug', 'Creating mock files in read-only workspace');

    // const startWkResult = await libparsec.clientStartWorkspace(loginResult.value, wkResult.value);
    // if (!startWkResult.ok) {
    //   window.nativeAPI.log('error', `Failed to start workspace: ${startWkResult.error.error}`);
    //   return;
    // }
    // for (const fileType in mockFiles.MockFileType) {
    //   const fileName = `document_${fileType}.${fileType.toLocaleLowerCase()}`;
    //   const openResult = await libparsec.workspaceOpenFile(startWkResult.value, `/${fileName}`, {
    //     write: true,
    //     truncate: true,
    //     create: true,
    //     createNew: true,
    //     read: false,
    //   });
    //   if (!openResult.ok) {
    //     window.nativeAPI.log('error', `Could not open file ${fileName}`);
    //     continue;
    //   }
    //   try {
    //     const content = await mockFiles.getMockFileContent(fileType as any);
    //     const writeResult = await libparsec.workspaceFdWrite(startWkResult.value, openResult.value, BigInt(0), content);
    //     if (!writeResult.ok) {
    //       window.nativeAPI.log('error', `Failed to write file ${fileName}`);
    //       continue;
    //     }
    //   } finally {
    //     await libparsec.workspaceFdClose(startWkResult.value, openResult.value);
    //   }
    // }

    const shareResult = await libparsec.clientShareWorkspace(
      loginResult.value,
      wkResult.value,
      aliceDevice.userId,
      parsec.WorkspaceRole.Reader,
    );
    if (!shareResult.ok) {
      window.nativeAPI.log('error', `Failed to share Bob's workspace with Alice: ${shareResult.error.error}`);
      return;
    }
    window.nativeAPI.log('debug', 'Read-only workspace created');
  } catch (e: any) {
    window.nativeAPI.log('error', `Error while creating read-only workspace: ${e}`);
  } finally {
    await libparsec.clientStop(loginResult.value);
  }
}
</script>
